import Button from 'components/actions/Button';
import { useEffect, useState } from 'react';

import ErrorMessage from './ErrorMessage';
import Fileinput from './FileInput';
import FilePreview from './FilePreview';
import { setHeaders } from './utils';
import './UploadScenarioForm_styles.scss';

interface UploadScenarioFormProps {
    domain: string | undefined;
}
/**
 * For future implementations:
 * - how to validate vin - and disable Upload Scenario button
 */
const UploadScenarioForm = ({ domain }: UploadScenarioFormProps) => {
    const [scenarioName, setScenarioName] = useState<string | undefined>(undefined);
    const [scenarioDescription, setScenarioDescription] = useState<string | undefined>(undefined);
    const [file, setFile] = useState<File | undefined>(undefined);
    const [fileContent, setFileContent] = useState<string | undefined>();
    const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
    // request flow
    const [isRequestSending, setIsRequestSending] = useState<boolean>(false);
    const [isRequestFailed, setIsRequestFailed] = useState<boolean>(false);

    const closeErrorMessage = () => {
        setIsRequestFailed(false);
    };

    const handlePreviewBox = () => {
        if (fileContent && !isPreviewOpen) {
            setIsPreviewOpen(true);
        } else if (fileContent && isPreviewOpen) {
            setIsPreviewOpen(false);
        }
    };

    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) {
            return;
        }
        const fileInput = files[0];
        setFile(fileInput);
    };

    const resetFile = () => {
        setFile(undefined);
        setFileContent(undefined);
        setIsPreviewOpen(false);
    };

    useEffect(() => {
        const readFile = () => {
            const fileReader = new FileReader();
            if (file) {
                fileReader.readAsText(file, 'UTF-8');
                fileReader.onload = readerEvent => {
                    if (readerEvent?.target?.result) {
                        setFileContent(readerEvent.target.result.toString());
                    }
                };
            }
        };

        readFile();
    }, [file]);

    const uploadSimulation = async () => {
        const url = `${domain}/simulator-adapter-service/api/v1/scenario`;
        setIsRequestFailed(false);
        setIsRequestSending(true);
        try {
            if (file && scenarioName && scenarioDescription) {
                const formData = new FormData();
                formData.append('scenarioFile', file);
                const scenarioMetadata = {
                    name: scenarioName,
                    description: scenarioDescription,
                };
                formData.append('scenario', new Blob([JSON.stringify(scenarioMetadata)], { type: 'application/json' }));

                await fetch(url, {
                    method: 'POST',
                    headers: await setHeaders(),
                    body: formData,
                });

                console.info('[Scenario] File to be uploaded', file);
            }
        } catch (error) {
            setIsRequestFailed(true);
            console.error(error);
        } finally {
            setIsRequestSending(false);
        }
    };

    return (
        <div className='UploadScenarioForm'>
            {isRequestFailed && <ErrorMessage message='Error while uploading scenario.' onClose={closeErrorMessage} />}
            <div className='UploadForm__form-grid'>
                <div>
                    <label className='UploadForm__field-label' htmlFor='inputScenarioName'>
                        Name
                    </label>
                    <input
                        data-testid='upload-scenario-name-input'
                        onChange={e => setScenarioName(e.currentTarget.value)}
                        type='text'
                        className='form-control'
                        id='inputScenarioName'
                        placeholder='e.g. Scenario Name'
                    />
                </div>
                <div>
                    <label className='UploadForm__field-label' htmlFor='inputScenarioDescription'>
                        Description
                    </label>
                    <input
                        data-testid='upload-scenario-description-input'
                        onChange={e => setScenarioDescription(e.currentTarget.value)}
                        type='text'
                        className='form-control'
                        id='inputScenarioDescription'
                        placeholder='e.g. Scenario between point A and point B'
                    />
                </div>
                <Fileinput file={file} onChange={handleFile} resetFile={resetFile} />
                <Button
                    data-testid='upload-simulation-scenario-button'
                    disabled={!scenarioDescription || !scenarioName || !file || isRequestSending}
                    className='btn btn-primary'
                    type='submit'
                    onClick={uploadSimulation}
                >
                    <div className='UploadForm__button-content'>
                        {/*
              Actual implementation: the simulation is triggered as soon the scenario is uploaded.
              Change button label and button icon.
            */}
                        <span className='rioglyph rioglyph-upload UploadForm__button-icon' />
                        <p>Upload Scenario</p>
                    </div>
                </Button>
            </div>
            <i
                data-testid='upload-scenario-preview-toggle'
                onClick={handlePreviewBox}
                className={`UploadScenarioForm__preview-toggle ${
                    fileContent && file
                        ? 'UploadScenarioForm__preview-toggle--active'
                        : 'UploadScenarioForm__preview-toggle--inactive'
                }`}
            >
                {isPreviewOpen ? 'Close' : 'Open'} file preview
            </i>
            {fileContent && isPreviewOpen && <FilePreview file={file} content={fileContent} />}
        </div>
    );
};

export default UploadScenarioForm;
